import React, { useState, useEffect } from 'react'
import {
  STRIPE_PRIMARY,
  STRIPE_TEXT_PRIMARY,
  STRIPE_TEXT_SECONDARY,
} from 'constants/stripe'
import { sharedStyles } from './shared-styles'

interface MetadataItem {
  key: string
  value: string
}

interface MetadataManagerProps {
  onMetadataChange?: (metadata: MetadataItem[]) => void
  initialMetadata?: MetadataItem[]
  lockedKeys?: string[] // Keys that cannot be modified
}

const MetadataManager: React.FC<MetadataManagerProps> = ({
  onMetadataChange,
  initialMetadata = [],
  lockedKeys = [],
}) => {
  const [metadata, setMetadata] = useState<MetadataItem[]>(initialMetadata)

  // Sync with prop changes
  useEffect(() => {
    setMetadata(initialMetadata)
  }, [initialMetadata])

  const removeMetadata = (index: number) => {
    const item = metadata[index]
    // Don't allow removal of locked keys
    if (lockedKeys.includes(item.key)) {
      return
    }
    const updatedMetadata = metadata.filter((_, i) => i !== index)
    setMetadata(updatedMetadata)
    onMetadataChange?.(updatedMetadata)
  }

  const updateMetadata = (
    index: number,
    field: 'key' | 'value',
    value: string
  ) => {
    const item = metadata[index]

    // Don't allow modification of locked keys (only the key field, not the value)
    if (field === 'key' && lockedKeys.includes(item.key)) {
      return
    }

    const updatedMetadata = metadata.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    )
    setMetadata(updatedMetadata)
    onMetadataChange?.(updatedMetadata)
  }

  return (
    <div style={sharedStyles.sectionContainer}>
      <h6 style={sharedStyles.sectionHeading}>Metadata</h6>

      {/* Existing metadata items */}
      {metadata.map((item, index) => {
        const isLocked = lockedKeys.includes(item.key)
        return (
          <div key={index} style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="text"
                value={item.key}
                onChange={(e) => updateMetadata(index, 'key', e.target.value)}
                placeholder="Key"
                disabled={isLocked}
                style={{
                  ...sharedStyles.textInput,
                  flex: 1,
                  backgroundColor: isLocked ? '#f8f9fa' : 'white',
                  color: isLocked ? STRIPE_TEXT_SECONDARY : STRIPE_TEXT_PRIMARY,
                  cursor: isLocked ? 'not-allowed' : 'text',
                }}
                title={
                  isLocked ? 'This field is locked and cannot be modified' : ''
                }
              />
              <input
                type="text"
                value={item.value}
                onChange={(e) => updateMetadata(index, 'value', e.target.value)}
                placeholder="Value"
                style={{
                  ...sharedStyles.textInput,
                  flex: 1,
                }}
              />
              {!isLocked && (
                <button
                  type="button"
                  onClick={() => removeMetadata(index)}
                  style={sharedStyles.removeButton}
                  title="Remove metadata"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default MetadataManager
